import React, { useCallback, useEffect, useState } from "react";
import { Button, Drawer, Table, Tag, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import { fetchWebhookIntegrationEvents } from "../../../../core/data/redux/services/webhookIntegrationApi";
import { getErrorMessage } from "../../../../core/data/redux/utils/errorUtils";
import { notify } from "../../../../services/toast.config";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import { EVENTS_PAGE_SIZE, processingStatusColors } from "../constants";

import type { WebhookEvent, WebhookIntegration } from "../types";

interface WebhookEventsDrawerProps {
  integration: WebhookIntegration | null;
  companyId: string;
  companyType: string;
  onClose: () => void;
}

const DRAWER_WIDTH = 820;

const columns: ColumnsType<WebhookEvent> = [
  {
    title: "Received",
    dataIndex: "created_at",
    key: "created_at",
    width: 190,
    render: (value: string) => humanReadableDate(value, true),
  },
  {
    title: "Event",
    dataIndex: "event_type",
    key: "event_type",
    render: (value: string | null) => <code>{value || "unknown"}</code>,
  },
  {
    title: "Status",
    dataIndex: "processing_status",
    key: "processing_status",
    width: 120,
    render: (value: WebhookEvent["processing_status"], record) => (
      <Tooltip title={record.error_message || undefined}>
        <Tag color={processingStatusColors[value] || "default"}>{value}</Tag>
      </Tooltip>
    ),
  },
  {
    title: "Note",
    dataIndex: "error_message",
    key: "error_message",
    ellipsis: true,
    render: (value: string | null) => value || "—",
  },
];

export const WebhookEventsDrawer: React.FC<WebhookEventsDrawerProps> = ({
  integration,
  companyId,
  companyType,
  onClose,
}) => {
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const loadEvents = useCallback(
    async (targetPage: number) => {
      if (!integration) return;
      setIsLoading(true);
      try {
        const data = await fetchWebhookIntegrationEvents(
          { companyId, companyType },
          integration.id,
          targetPage,
          EVENTS_PAGE_SIZE,
        );
        setEvents(data.events);
        setTotal(data.pagination.total);
        setPage(targetPage);
      } catch (error) {
        notify.error(getErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    },
    [integration, companyId, companyType],
  );

  useEffect(() => {
    if (integration) loadEvents(1);
  }, [integration, loadEvents]);

  return (
    <Drawer
      open={Boolean(integration)}
      onClose={onClose}
      width={DRAWER_WIDTH}
      title={`Recent Events · ${integration?.name || ""}`}
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => loadEvents(page)} loading={isLoading}>
          Refresh
        </Button>
      }
      destroyOnClose>
      <Table<WebhookEvent>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={events}
        loading={isLoading}
        expandable={{
          expandedRowRender: (record) => (
            <pre className="mb-0" style={{ fontSize: 11, maxHeight: 320, overflow: "auto" }}>
              {JSON.stringify(record.payload, null, 2)}
            </pre>
          ),
        }}
        pagination={{
          current: page,
          pageSize: EVENTS_PAGE_SIZE,
          total,
          onChange: loadEvents,
          showTotal: (count) => `${count} events`,
        }}
      />
    </Drawer>
  );
};
